import { useEffect, useState } from 'react'
import { Navigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useNavigate } from '@tanstack/react-router'
import { apis } from '@/modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { useTar } from '@/modules'
import { useAuth } from '@/hooks/useAuth'
import { StandardLoader } from '@/components/StandardLoader'
import { InviteGateSignup } from './InviteGateSignup'
import { InviteGateAlreadyRegistered } from './InviteGateAlreadyRegistered'
import { InviteGatePublicAssignmentChoice } from './InviteGatePublicAssignmentChoice'
import { InviteGateWrongAccount } from './InviteGateWrongAccount'
import { InviteGateJoinPublicAssignment } from './InviteGateJoinPublicAssignment'

type FetchState =
  | { status: 'loading' }
  | { status: 'ok'; details: UserService.InvitationDetails }
  | { status: 'error'; errorCode?: string; message?: string }

// Module-scoped cache survives the auth-state-change remount caused by
// SideBarLayout's `key={isAuthenticated ? 'authenticated' : 'unauthenticated'}`,
// which would otherwise refire the fetch with a stale token (returning
// "Invitation has already been accepted") right before the user navigates away.
const inFlight = new Map<string, Promise<FetchState>>()
const resolved = new Map<string, FetchState>()

interface InvitePageProps {
  token?: string
}

type ErrorKey =
  | 'invite.error.expired'
  | 'invite.error.accepted'
  | 'invite.error.notFound'
  | 'invite.error.generic'

const errorCopyKey = (errorCode?: string): ErrorKey => {
  switch (errorCode) {
    case 'expiredToken':
      return 'invite.error.expired'
    case 'acceptedToken':
      return 'invite.error.accepted'
    case 'userNotFound':
      return 'invite.error.notFound'
    default:
      return 'invite.error.generic'
  }
}

export const InvitePage = ({ token }: InvitePageProps) => {
  const { t } = useTar()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [state, setState] = useState<FetchState>(() =>
    token ? resolved.get(token) ?? { status: 'loading' } : { status: 'error', errorCode: 'invalidToken' }
  )

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', errorCode: 'invalidToken' })
      return
    }
    const cached = resolved.get(token)
    if (cached) {
      setState(cached)
      return
    }
    let cancelled = false
    const existing = inFlight.get(token)
    const promise = existing ?? (async (): Promise<FetchState> => {
      try {
        const { data } = await apis.userService.invitationTokenGet(token)
        const next: FetchState = { status: 'ok', details: data }
        resolved.set(token, next)
        return next
      } catch (err) {
        if (err instanceof AxiosError) {
          const errorCode = err.response?.data?.errorCode
          if (errorCode === 'alreadyJoinedPublicAssignment') {
            // Don't cache — the redirect handles it once.
            return { status: 'error', errorCode }
          }
          const next: FetchState = { status: 'error', errorCode, message: err.response?.data?.error }
          resolved.set(token, next)
          return next
        }
        const next: FetchState = { status: 'error' }
        resolved.set(token, next)
        return next
      } finally {
        inFlight.delete(token)
      }
    })()
    if (!existing) inFlight.set(token, promise)
    promise.then(next => {
      if (cancelled) return
      if (next.status === 'error' && next.errorCode === 'alreadyJoinedPublicAssignment') {
        navigate({ to: '/challenges/available' })
        return
      }
      setState(next)
    })
    return () => {
      cancelled = true
    }
  }, [token, navigate])

  if (!token) return <Navigate to="/" />

  if (state.status === 'loading') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
        <StandardLoader size="md" />
        <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
        <div className="bg-error/10 border border-error/20 rounded-lg p-6 max-w-md text-center">
          <h2 className="text-lg font-semibold text-error mb-2">
            {t('invite.error.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(errorCopyKey(state.errorCode))}
          </p>
        </div>
      </div>
    )
  }

  const details = state.details

  // Logged-in user lands on an invite page → branch on token type and identity match
  if (isAuthenticated && user?.email) {
    if (details.isPublicAssignment) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
          <InviteGateJoinPublicAssignment details={details} token={token} />
        </div>
      )
    }
    if (details.email && details.email.toLowerCase() !== user.email.toLowerCase()) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
          <InviteGateWrongAccount details={details} loggedInEmail={user.email} />
        </div>
      )
    }
    // Logged in as the right user — claim is fired by the AlreadyRegisteredView.
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
        <InviteGateAlreadyRegistered details={details} token={token} />
      </div>
    )
  }

  // Logged-out user
  if (details.isPublicAssignment) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
        <InviteGatePublicAssignmentChoice details={details} token={token} />
      </div>
    )
  }
  if (details.recipientHasAccount) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
        <InviteGateAlreadyRegistered details={details} token={token} />
      </div>
    )
  }
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
      <InviteGateSignup details={details} token={token} />
    </div>
  )
}
